// src/Payroll_Management/PayRoll_Report/EmployeeResignReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const EmployeeResignReportPrint = () => {
  const q = useQuery();

  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const employeeId = q.get("employeeId") || q.get("empId") || ""; // support both

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);
  const [rows, setRows] = useState([]); // if transfer join returns multiple rows

  // ✅ company header (same style as All print)
  const company = {
    name: "Nissi Infotech",
    sub1: "CIVIL CONSTRUCTION",
    addr1: "3/42-12, Nagappa colony, Kavundampalayam, Coimbatore - 641030, Tamil Nadu.",
    addr2: "Phone No : 9003744644",
    addr3: "infraconssoft-implementations",
    logo: "/assets/company-logo.png",
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setErr("");

        if (!String(finYearId).trim()) {
          setErr("finYearId is missing. Please select Financial Year.");
          setRow(null);
          setRows([]);
          return;
        }
        if (!String(employeeId).trim()) {
          setErr("employeeId is missing in URL. Example: ?employeeId=3250");
          setRow(null);
          setRows([]);
          return;
        }

        const res = await axios.get(`${BASE_URL}/api/employee-resignation-report/detail`, {
          params: {
            finYearId: Number(finYearId),
            employeeId: Number(employeeId),
          },
        });

        if (!res?.data?.success) {
          setErr(res?.data?.message || "Failed to load employee resign report details");
          setRow(null);
          setRows([]);
          return;
        }

        setRow(res.data.row || null);
        setRows(res.data.rows || []);
      } catch (e) {
        setErr(e?.response?.data?.message || e.message || "Failed to load employee resign report details");
        setRow(null);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [finYearId, employeeId]);

  // auto print after load
  useEffect(() => {
    if (!loading && !err && row) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
  }, [loading, err, row]);

  const printedOn = new Date().toLocaleString();

  // helpers
  const v = (x, fallback = " - ") => {
    const s = (x ?? "").toString().trim();
    return s ? s : fallback;
  };

  // Some columns from SP
  // row.fld_branchname, fld_employee, fld_lname, fld_departmentname, fld_designationname, DOJ, modifieddate, resigndate,
  // fld_mobile, fld_emailid, fld_accountno, fld_permaddress, fld_tempaddress, fld_countryname, fld_statename, fld_districtname, fld_cityname,
  // dob, fld_preexpyrs, fld_preexpmonths, fld_pf, fld_pfno, fld_pfpercentage, fld_esi, fld_ESIno, fld_qualificationname, fld_completionyr,
  // fld_PANno, fld_esipercentage, fld_dailybata, fld_gradename, fld_SSNno, fld_salarybasisname, fld_incometax, fld_drivinglicnum,
  // expdate, fld_drivinglicexpdate, fld_SpecialHRA, transferFrom, transferTo, fromDate, toDate, monthlysalary, rem.fld_details as fld_details

  return (
    <div className="print-root">
      <style>{`
        /* ✅ Wide detail report => landscape */
        @page { size: A4 landscape; margin: 10mm; }

        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: "Times New Roman", serif; color: #000; }

        .no-print { display: flex; gap: 8px; justify-content: flex-end; margin: 10px 0; }
        @media print { .no-print { display: none !important; } }

        .print-root {
          min-height: 100vh;
          overflow-y: auto;
          background: #fff;
          padding: 12px;
        }

        .sheet {
          width: 100%;
          border: 1px solid #000;
          padding: 10px;
        }

        .header-box {
          border: 1px solid #000;
          padding: 10px;
        }

        .header-grid {
          display: grid;
          grid-template-columns: 120px 1fr;
          gap: 10px;
          align-items: center;
        }

        .logo-box {
          border: 1px solid #000;
          width: 120px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .logo-box img { width: 100%; height: 100%; object-fit: contain; }

        .company-title { text-align: center; font-weight: bold; font-size: 22px; margin: 0; }
        .company-sub { text-align: center; font-weight: bold; font-size: 14px; margin: 4px 0; }
        .company-addr { text-align: center; font-size: 12px; margin: 2px 0; }

        .report-title {
          margin-top: 10px;
          border-top: 1px solid #000;
          padding-top: 8px;
          text-align: center;
          font-size: 14px;
          font-weight: bold;
          letter-spacing: 0.5px;
        }

        .meta-row {
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          gap: 10px;
          flex-wrap: wrap;
        }

        .section {
          border: 1px solid #000;
          margin-top: 10px;
        }

        .section-title {
          font-weight: bold;
          font-size: 12px;
          padding: 6px 8px;
          border-bottom: 1px solid #000;
          background: #f2f2f2;
        }

        .kv {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .kv td {
          border: 1px solid #000;
          padding: 6px 8px;
          vertical-align: top;
        }

        .k { font-weight: bold; width: 16%; white-space: nowrap; }
        .val { width: 34%; }

        .table-scroll {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          margin-top: 10px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        th, td {
          border: 1px solid #000;
          padding: 6px 6px;
          vertical-align: middle;
        }

        thead th {
          font-weight: bold;
          text-align: center;
          white-space: nowrap;
        }

        td.center { text-align: center; }
        td.right { text-align: right; }

        .foot-note { margin-top: 8px; font-size: 12px; }

        /* ✅ PRINT: fit tight */
        @media print {
          .print-root { overflow: visible !important; padding: 0 !important; }
          table { font-size: 10px !important; }
          th, td { padding: 3px 3px !important; }
          thead th { white-space: normal !important; }
        }
      `}</style>

      <div className="no-print">
        <button
          onClick={() => window.print()}
          style={{ padding: "8px 12px", fontWeight: "bold", border: "1px solid #000", background: "#000", color: "#fff" }}
        >
          Print
        </button>
        <button
          onClick={() => window.close()}
          style={{ padding: "8px 12px", fontWeight: "bold", border: "1px solid #000", background: "#eee", color: "#000" }}
        >
          Close
        </button>
      </div>

      <div className="sheet">
        {/* Header */}
        <div className="header-box">
          <div className="header-grid">
            <div className="logo-box">
              <img
                src={company.logo}
                alt="Company Logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div>
              <div className="company-title">{company.name}</div>
              <div className="company-sub">{company.sub1}</div>
              <div className="company-addr">{company.addr1}</div>
              <div className="company-addr">{company.addr2}</div>
              {company.addr3 ? <div className="company-addr">{company.addr3}</div> : null}
            </div>
          </div>

          <div className="report-title">EMPLOYEE RESIGNATION DETAIL REPORT</div>

          <div className="meta-row">
            <div>
              <b>Emp ID:</b> {v(employeeId)} &nbsp;&nbsp; <b>Emp Code:</b> {v(row?.fld_Employeecode)} &nbsp;&nbsp;{" "}
              <b>Emp Name:</b> {v(row?.EmployeeName || row?.fld_employee)}
            </div>
            <div>
              <b>Printed On:</b> {printedOn}
            </div>
          </div>

          <div className="meta-row">
            <div>
              <b>Branch:</b> {v(row?.fld_branchname || row?.fld_BranchName)} &nbsp;&nbsp; <b>Dept:</b>{" "}
              {v(row?.fld_departmentname)} &nbsp;&nbsp; <b>Desig:</b> {v(row?.fld_designationname)} &nbsp;&nbsp;{" "}
              <b>Grade:</b> {v(row?.fld_gradename)}
            </div>
            <div>
              <b>DOJ:</b> {v(row?.DOJ)} &nbsp;&nbsp; <b>Resign:</b> {v(row?.resigndate)} &nbsp;&nbsp; <b>Modified:</b>{" "}
              {v(row?.modifieddate)}
            </div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="foot-note">Loading...</div>
        ) : err ? (
          <div className="foot-note" style={{ color: "red" }}>
            {err}
          </div>
        ) : !row ? (
          <div className="foot-note">No Record Found</div>
        ) : (
          <>
            {/* Employee Info */}
            <div className="section">
              <div className="section-title">Employee Information</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">Employee Code</td>
                    <td className="val">{v(row?.fld_Employeecode)}</td>
                    <td className="k">Employee Name</td>
                    <td className="val">{v(row?.EmployeeName || row?.fld_employee)} {v(row?.fld_lname, "")}</td>
                  </tr>
                  <tr>
                    <td className="k">Father Name</td>
                    <td className="val">{v(row?.fld_fathername)}</td>
                    <td className="k">Gender</td>
                    <td className="val">{v(row?.fld_gender)}</td>
                  </tr>
                  <tr>
                    <td className="k">DOB</td>
                    <td className="val">{v(row?.dob)}</td>
                    <td className="k">Marital Status</td>
                    <td className="val">{v(row?.fld_maritalstatus)}</td>
                  </tr>
                  <tr>
                    <td className="k">Mobile</td>
                    <td className="val">{v(row?.fld_mobile)}</td>
                    <td className="k">Email</td>
                    <td className="val">{v(row?.fld_emailid)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Job / Salary */}
            <div className="section">
              <div className="section-title">Job / Salary Details</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">Department</td>
                    <td className="val">{v(row?.fld_departmentname)}</td>
                    <td className="k">Designation</td>
                    <td className="val">{v(row?.fld_designationname)}</td>
                  </tr>
                  <tr>
                    <td className="k">Shift</td>
                    <td className="val">{v(row?.fld_shiftname)}</td>
                    <td className="k">Salary Basis</td>
                    <td className="val">{v(row?.fld_salarybasisname)}</td>
                  </tr>
                  <tr>
                    <td className="k">Gross Salary</td>
                    <td className="val">{v(row?.fld_grosssalary)}</td>
                    <td className="k">Daily Batta</td>
                    <td className="val">{v(row?.fld_dailybata)}</td>
                  </tr>
                  <tr>
                    <td className="k">Special HRA</td>
                    <td className="val">{v(row?.fld_SpecialHRA)}</td>
                    <td className="k">Income Tax</td>
                    <td className="val">{v(row?.fld_incometax)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* PF / ESI */}
            <div className="section">
              <div className="section-title">PF / ESI Details</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">PF Applicable</td>
                    <td className="val">{v(row?.fld_pf)}</td>
                    <td className="k">PF No</td>
                    <td className="val">{v(row?.fld_pfno)}</td>
                  </tr>
                  <tr>
                    <td className="k">PF %</td>
                    <td className="val">{v(row?.fld_pfpercentage)}</td>
                    <td className="k">ESI Applicable</td>
                    <td className="val">{v(row?.fld_esi)}</td>
                  </tr>
                  <tr>
                    <td className="k">ESI No</td>
                    <td className="val">{v(row?.fld_ESIno)}</td>
                    <td className="k">ESI %</td>
                    <td className="val">{v(row?.fld_esipercentage)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Address */}
            <div className="section">
              <div className="section-title">Address</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">Permanent</td>
                    <td className="val" colSpan={3}>{v(row?.fld_permaddress)}</td>
                  </tr>
                  <tr>
                    <td className="k">Temporary</td>
                    <td className="val" colSpan={3}>{v(row?.fld_tempaddress)}</td>
                  </tr>
                  <tr>
                    <td className="k">Country</td>
                    <td className="val">{v(row?.fld_countryname)}</td>
                    <td className="k">State</td>
                    <td className="val">{v(row?.fld_statename)}</td>
                  </tr>
                  <tr>
                    <td className="k">District</td>
                    <td className="val">{v(row?.fld_districtname)}</td>
                    <td className="k">City</td>
                    <td className="val">{v(row?.fld_cityname)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Qualification */}
            <div className="section">
              <div className="section-title">Qualification</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">Qualification</td>
                    <td className="val">{v(row?.fld_qualificationname)}</td>
                    <td className="k">Completion Year</td>
                    <td className="val">{v(row?.fld_completionyr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Bank / IDs */}
            <div className="section">
              <div className="section-title">Bank / IDs</div>
              <table className="kv">
                <tbody>
                  <tr>
                    <td className="k">A/C No</td>
                    <td className="val">{v(row?.fld_accountno)}</td>
                    <td className="k">PAN No</td>
                    <td className="val">{v(row?.fld_PANno)}</td>
                  </tr>
                  <tr>
                    <td className="k">SSN No</td>
                    <td className="val">{v(row?.fld_SSNno)}</td>
                    <td className="k">Driving Lic No</td>
                    <td className="val">{v(row?.fld_drivinglicnum)}</td>
                  </tr>
                  <tr>
                    <td className="k">Driving Lic Exp</td>
                    <td className="val">{v(row?.expdate)}</td>
                    <td className="k">Driving Lic Exp(106)</td>
                    <td className="val">{v(row?.fld_drivinglicexpdate)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Transfer History (if multiple rows) */}
            {rows.length > 1 && (
              <div className="section">
                <div className="section-title">Transfer History</div>
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Transfer From</th>
                        <th>Transfer To</th>
                        <th>Work Period From</th>
                        <th>Work Period To</th>
                        <th>Monthly Salary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i}>
                          <td className="center">{i + 1}</td>
                          <td className="center">{v(r.transferFrom)}</td>
                          <td className="center">{v(r.transferTo)}</td>
                          <td className="center">{v(r.fromDate)}</td>
                          <td className="center">{v(r.toDate)}</td>
                          <td className="right">{v(r.monthlysalary)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Remarks */}
            {String(row?.fld_details || "").trim() && (
              <div className="section">
                <div className="section-title">Remarks</div>
                <table className="kv">
                  <tbody>
                    <tr>
                      <td className="val" colSpan={4}>{row.fld_details}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="foot-note">
              <b>Note:</b> This is a system generated report.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeResignReportPrint;
